// src/api/api.js
const DEFAULT_BASE_URLS = [
  'http://localhost:5000/api',
  'https://sedyambackend.onrender.com/api',
];

const normalizeApiBase = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(withProtocol);
    const cleanPath = url.pathname.replace(/\/+$/, '');
    url.pathname = cleanPath.endsWith('/api') ? cleanPath : `${cleanPath || ''}/api`;
    return url.toString().replace(/\/+$/, '');
  } catch {
    return null;
  }
};

const isLocalHostUrl = (baseUrl) => /\/\/(localhost|127\.0\.0\.1)(?::\d+)?\//i.test(baseUrl);

const getBaseUrls = () => {
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const runningLocally = /^(localhost|127\.0\.0\.1)$/i.test(currentHost);
  const configured = String(process.env.REACT_APP_API_URL || '')
    .split(/(?:\|\||,|\s+)/)
    .map((item) => normalizeApiBase(item))
    .filter(Boolean);

  const normalizedDefaults = DEFAULT_BASE_URLS.map((item) => normalizeApiBase(item)).filter(Boolean);

  if (runningLocally) {
    return [...new Set([...configured, ...normalizedDefaults])];
  }

  if (configured.length > 0) {
    return [...new Set(configured)];
  }

  return [...new Set(normalizedDefaults.filter((baseUrl) => !isLocalHostUrl(baseUrl)))];
};

const BASE_URLS = getBaseUrls();
export const API_BASE_URLS = BASE_URLS;
export const SOCKET_URLS = BASE_URLS.map((baseUrl) => baseUrl.replace(/\/api$/, ''));
const LAST_SUCCESSFUL_API_BASE_KEY = 'sedyam_api_base';
const LAST_SUCCESSFUL_SESSION_API_BASE_KEY = 'sedyam_session_api_base';
const DEFAULT_REQUEST_TIMEOUT_MS = 10000;
const AUTH_REQUEST_TIMEOUT_MS = 20000;
const LOCALHOST_FAST_FAIL_TIMEOUT_MS = 8000;
const DISTANCE_PREVIEW_TIMEOUT_MS = 30000;
const FAILED_BASE_COOLDOWN_MS = 60000;
const RETRYABLE_GET_ATTEMPTS = 2;
const RETRY_BACKOFF_MS = 1200;
const RETRY_TIMEOUT_MULTIPLIER = 2;
const inflightRequests = new Map();
const recentBaseFailures = new Map();

const getToken = () => localStorage.getItem('sedyam_token');

const getStoredProfile = () => {
  try {
    const raw = localStorage.getItem('sedyam_profile');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const OPERATIONAL_ORDER_ROLES = new Set([
  'delivery',
  'farmer',
  'dealer',
  'manager',
  'ceo',
  'admin',
  'super_admin',
]);

const shouldRetryNextBaseForEmptyOperationalOrders = (method, endpoint, data, orderedBaseUrls) => {
  if (String(method || '').toUpperCase() !== 'GET') return false;
  if (!String(endpoint || '').startsWith('/orders')) return false;
  if (!Array.isArray(orderedBaseUrls) || orderedBaseUrls.length <= 1) return false;

  const profile = getStoredProfile();
  const role = String(profile?.role || '').trim().toLowerCase();
  if (!OPERATIONAL_ORDER_ROLES.has(role)) return false;

  const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : null;
  if (!rows) return false;
  return rows.length === 0;
};

const getStoredPreferredBase = () => {
  try {
    const stored = localStorage.getItem(LAST_SUCCESSFUL_API_BASE_KEY);
    return stored && BASE_URLS.includes(stored) ? stored : null;
  } catch {
    return null;
  }
};

const shouldUseSessionPreferredBase = (endpoint = '') => {
  const normalizedEndpoint = String(endpoint || '');
  return (
    normalizedEndpoint.startsWith('/auth/') ||
    normalizedEndpoint.startsWith('/orders') ||
    normalizedEndpoint.startsWith('/farmers/me') ||
    normalizedEndpoint.startsWith('/dealers/me') ||
    normalizedEndpoint.startsWith('/staff')
  );
};

const getStoredSessionPreferredBase = (endpoint = '') => {
  if (!shouldUseSessionPreferredBase(endpoint)) return null;

  try {
    const stored = localStorage.getItem(LAST_SUCCESSFUL_SESSION_API_BASE_KEY);
    return stored && BASE_URLS.includes(stored) ? stored : null;
  } catch {
    return null;
  }
};

const shouldRememberPreferredBaseForEndpoint = (endpoint = '') => {
  const normalizedEndpoint = String(endpoint || '');
  if (!normalizedEndpoint) return false;
  if (normalizedEndpoint.startsWith('/location-directory')) return false;
  if (normalizedEndpoint.startsWith('/staff/ifsc/')) return false;
  return true;
};

const rememberPreferredBase = (baseUrl, endpoint = '') => {
  if (!shouldRememberPreferredBaseForEndpoint(endpoint)) return;
  try {
    localStorage.setItem(LAST_SUCCESSFUL_API_BASE_KEY, baseUrl);
  } catch {
    // Ignore storage failures.
  }
};

const rememberSessionPreferredBase = (baseUrl, endpoint = '') => {
  if (!shouldUseSessionPreferredBase(endpoint)) return;

  try {
    localStorage.setItem(LAST_SUCCESSFUL_SESSION_API_BASE_KEY, baseUrl);
  } catch {
    // Ignore storage failures.
  }
};

const markBaseFailure = (baseUrl) => {
  recentBaseFailures.set(baseUrl, Date.now());
};

const clearBaseFailure = (baseUrl) => {
  recentBaseFailures.delete(baseUrl);
};

const isBaseOnCooldown = (baseUrl) => {
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const runningLocally = /^(localhost|127\.0\.0\.1)$/i.test(currentHost);
  if (runningLocally && isLocalHostUrl(baseUrl)) {
    return false;
  }

  const failedAt = recentBaseFailures.get(baseUrl);
  if (!failedAt) return false;
  if (Date.now() - failedAt > FAILED_BASE_COOLDOWN_MS) {
    recentBaseFailures.delete(baseUrl);
    return false;
  }
  return true;
};

const getOrderedBaseUrls = (_endpoint = '', options = {}) => {
  const endpoint = String(_endpoint || '');
  const storedPreferred = getStoredPreferredBase();
  const storedSessionPreferred = getStoredSessionPreferredBase(endpoint);
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const runningLocally = /^(localhost|127\.0\.0\.1)$/i.test(currentHost);
  const forceConfiguredFirst = /^true$/i.test(String(process.env.REACT_APP_FORCE_REMOTE_API || ''));
  const configured = String(process.env.REACT_APP_API_URL || '')
    .split(/(?:\|\||,|\s+)/)
    .map((item) => normalizeApiBase(item))
    .filter(Boolean);

  const ordered = [...BASE_URLS].sort((a, b) => {
    const aLocal = isLocalHostUrl(a);
    const bLocal = isLocalHostUrl(b);
    const aConfigured = configured.includes(a);
    const bConfigured = configured.includes(b);

    if (runningLocally) {
      if (!forceConfiguredFirst) {
        // When the app is running on localhost, try the local backend first.
        // If it is unavailable, the request still falls through to remote bases.
        if (aLocal && !bLocal) return -1;
        if (!aLocal && bLocal) return 1;
      }

      if (storedSessionPreferred && a === storedSessionPreferred) return -1;
      if (storedSessionPreferred && b === storedSessionPreferred) return 1;

      if (storedPreferred && a === storedPreferred) return -1;
      if (storedPreferred && b === storedPreferred) return 1;

      if (aConfigured && !bConfigured) return -1;
      if (!aConfigured && bConfigured) return 1;

      // Otherwise keep localhost first for zero-config development.
      if (configured.length === 0) {
        if (aLocal && !bLocal) return -1;
        if (!aLocal && bLocal) return 1;
      }

      // Within the same configuration bucket, prefer localhost for local workflows.
      if (aLocal && !bLocal && aConfigured === bConfigured) return -1;
      if (!aLocal && bLocal && aConfigured === bConfigured) return 1;

      return 0;
    }

    if (storedSessionPreferred && a === storedSessionPreferred) return -1;
    if (storedSessionPreferred && b === storedSessionPreferred) return 1;

    if (storedPreferred && a === storedPreferred) return -1;
    if (storedPreferred && b === storedPreferred) return 1;

    if (!aLocal && bLocal) return -1;
    if (aLocal && !bLocal) return 1;
    return 0;
  });

  const uniqueOrdered = [...new Set(ordered)];
  const readyBases = uniqueOrdered.filter((baseUrl) => !isBaseOnCooldown(baseUrl));

  const activeBases = readyBases.length > 0 ? readyBases : uniqueOrdered;
  const localBases = activeBases.filter((baseUrl) => isLocalHostUrl(baseUrl));
  const remoteBases = activeBases.filter((baseUrl) => !isLocalHostUrl(baseUrl));

  if (options.localOnly) return localBases;
  if (options.remoteOnly && remoteBases.length > 0) return remoteBases;
  if (options.preferRemote && remoteBases.length > 0) {
    const localBases = activeBases.filter((baseUrl) => isLocalHostUrl(baseUrl));
    return [...remoteBases, ...localBases];
  }

  return activeBases;
};

export const getOrderedSocketUrls = () => getOrderedBaseUrls().map((baseUrl) => baseUrl.replace(/\/api$/, ''));

const fetchWithTimeout = async (url, options, timeoutMs) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const sleep = (ms) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

class APIError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
  }
}

const lookupIfscDirect = async (ifsc) => {
  const normalizedIfsc = String(ifsc || '').trim().toUpperCase();
  if (!normalizedIfsc) {
    throw new APIError('IFSC code is required.', 400, { message: 'IFSC code is required.' });
  }

  try {
    const response = await fetchWithTimeout(
      `https://ifsc.razorpay.com/${encodeURIComponent(normalizedIfsc)}`,
      {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      },
      8000
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new APIError('Invalid IFSC code.', 404, { message: 'Invalid IFSC code.' });
      }
      throw new APIError('Unable to verify IFSC code right now.', response.status, {
        message: 'Unable to verify IFSC code right now.',
      });
    }

    const data = await response.json();
    return {
      success: true,
      data: {
        ifscCode: String(data?.IFSC || normalizedIfsc).trim().toUpperCase(),
        branch: String(data?.BRANCH || '').trim(),
        bankName: String(data?.BANK || '').trim(),
        city: String(data?.CITY || '').trim(),
        district: String(data?.DISTRICT || '').trim(),
        state: String(data?.STATE || '').trim(),
        address: String(data?.ADDRESS || '').trim(),
      },
    };
  } catch (error) {
    if (error instanceof APIError) throw error;
    throw new APIError('Unable to verify IFSC code right now.', 0, {
      message: 'Unable to verify IFSC code right now.',
    });
  }
};

export const bankingAPI = {
  lookupIfsc: async (ifsc) => {
    try {
      return await lookupIfscDirect(ifsc);
    } catch (directError) {
      return request('GET', `/staff/ifsc/${encodeURIComponent(ifsc || '')}`);
    }
  },
};

const getRequestBasePreference = (method, endpoint, body) => {
  const normalizedEndpoint = String(endpoint || '');
  const normalizedMethod = String(method || '').toUpperCase();
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const localPaymentTesting =
    /^(localhost|127\.0\.0\.1)$/i.test(currentHost) ||
    String(process.env.NODE_ENV || '').toLowerCase() === 'development';

  // Local sessions must read and write account data through the local backend
  // too; a remembered remote session host can otherwise split profile updates.
  if (localPaymentTesting && normalizedEndpoint.startsWith('/auth/')) {
    return { localOnly: true };
  }

  if (normalizedEndpoint === '/payment/verify-checkout' || normalizedEndpoint.startsWith('/payment/checkout-recoveries/')) {
    if (localPaymentTesting) return { localOnly: true };
    return { preferRemote: true, remoteOnly: false };
  }

  if (normalizedEndpoint.startsWith('/payment/')) {
    if (localPaymentTesting) return { localOnly: true };
    return { preferRemote: true, remoteOnly: true };
  }

  if (normalizedMethod === 'POST' && normalizedEndpoint === '/orders') {
    const payload = body && typeof body === 'object' ? body : {};
    const hasPaymentPayload = Boolean(
      payload.razorpayOrderId ||
      payload.razorpayPaymentId ||
      payload.razorpaySignature ||
      payload.cashfreeOrderId ||
      payload.cashfreePaymentId ||
      payload.paymentMethod === 'Razorpay' ||
      payload.paymentMethod === 'Cashfree' ||
      payload.paymentStatus === 'Paid_In_Full'
    );
    if (localPaymentTesting && hasPaymentPayload) return { localOnly: true };
    return { preferRemote: hasPaymentPayload, remoteOnly: hasPaymentPayload };
  }

  if (normalizedEndpoint === '/orders/delivery-distance-preview') {
    return { preferRemote: false, remoteOnly: false };
  }

  if (normalizedEndpoint.startsWith('/orders')) {
    return { preferRemote: false, remoteOnly: false };
  }

  return { preferRemote: false, remoteOnly: false };
};

const shouldRetryNextBaseForAuthMismatch = (endpoint, status, orderedBaseUrls) => {
  const normalizedEndpoint = String(endpoint || '');
  if (!normalizedEndpoint.startsWith('/orders')) return false;
  if (![401, 403].includes(Number(status))) return false;
  return Array.isArray(orderedBaseUrls) && orderedBaseUrls.length > 1;
};

const shouldRetryNextBaseForDistancePreview = (endpoint, status, data, orderedBaseUrls) => {
  const normalizedEndpoint = String(endpoint || '');
  if (normalizedEndpoint !== '/orders/delivery-distance-preview') return false;
  if (!Array.isArray(orderedBaseUrls) || orderedBaseUrls.length <= 1) return false;

  const message = String(data?.message || '').toLowerCase();
  if (Number(status) === 503) return true;
  if (message.includes('google maps api key is missing')) return true;
  if (message.includes('exact delivery distance is unavailable')) return true;
  if (message.includes('could not resolve the exact delivery route')) return true;
  return false;
};

const shouldRetryNextBaseForSuccessfulDistancePreview = (endpoint, data, orderedBaseUrls) => {
  const normalizedEndpoint = String(endpoint || '');
  if (normalizedEndpoint !== '/orders/delivery-distance-preview') return false;
  if (!Array.isArray(orderedBaseUrls) || orderedBaseUrls.length <= 1) return false;

  const preview = data && typeof data === 'object' ? (data.data || data) : null;
  return String(preview?.method || '').toLowerCase() === 'configuration_required';
};

const shouldRetryNextBaseForLocationDirectoryDistricts = (endpoint, data, orderedBaseUrls) => {
  const normalizedEndpoint = String(endpoint || '');
  if (!normalizedEndpoint.startsWith('/location-directory/districts')) return false;
  if (!Array.isArray(orderedBaseUrls) || orderedBaseUrls.length <= 1) return false;

  const payload = data && typeof data === 'object' ? (data.data || data) : {};
  const districts = Array.isArray(payload?.districts) ? payload.districts : [];
  const rowCount = Number(payload?.rowCount || 0);

  if (districts.length === 0) return true;
  return rowCount > districts.length * 5;
};

const request = async (method, endpoint, body = null, isFormData = false) => {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body && !isFormData) headers['Content-Type'] = 'application/json';
  const requestBody = body ? (isFormData ? body : JSON.stringify(body)) : null;
  let lastNetworkError = null;
  let lastRetriableApiError = null;
  const basePreference = getRequestBasePreference(method, endpoint, body);
  const orderedBaseUrls = getOrderedBaseUrls(endpoint, {
    localOnly: basePreference.localOnly,
    preferRemote: basePreference.preferRemote,
    remoteOnly: basePreference.remoteOnly,
  });
  const isDistancePreviewEndpoint = String(endpoint || '') === '/orders/delivery-distance-preview';
  const isLocationDirectoryEndpoint = String(endpoint || '').startsWith('/location-directory');
  const timeoutMs = String(endpoint || '').startsWith('/auth/')
    ? AUTH_REQUEST_TIMEOUT_MS
    : isDistancePreviewEndpoint
      ? DISTANCE_PREVIEW_TIMEOUT_MS
    : isLocationDirectoryEndpoint
      ? 30000
      : DEFAULT_REQUEST_TIMEOUT_MS;
  const requestKey =
    method === 'GET'
      ? JSON.stringify({
          method,
          endpoint,
          token,
          params: body,
        })
      : null;

  if (requestKey && inflightRequests.has(requestKey)) {
    return inflightRequests.get(requestKey);
  }

  const executeRequest = async () => {
    const maxAttempts = method === 'GET' ? RETRYABLE_GET_ATTEMPTS : 1;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      const requestTimeoutMs =
        attempt === 0 ? timeoutMs : Math.min(timeoutMs * RETRY_TIMEOUT_MULTIPLIER, 30000);

      for (const baseUrl of orderedBaseUrls) {
        try {
          const effectiveTimeoutMs =
            orderedBaseUrls.length > 1 && isLocalHostUrl(baseUrl)
              ? (isLocationDirectoryEndpoint || isDistancePreviewEndpoint)
                ? requestTimeoutMs
                : Math.min(requestTimeoutMs, LOCALHOST_FAST_FAIL_TIMEOUT_MS)
              : requestTimeoutMs;

          const res = await fetchWithTimeout(
            `${baseUrl}${endpoint}`,
            {
              method,
              headers,
              body: requestBody,
            },
            effectiveTimeoutMs
          );

          const raw = await res.text();
          let data = {};
          try {
            data = raw ? JSON.parse(raw) : {};
          } catch {
            data = { message: raw || 'Request failed' };
          }

          if (!res.ok) {
            const message = String(data?.message || '');
            const routeNotFound = res.status === 404 && /route not found/i.test(message);

            if (routeNotFound && orderedBaseUrls.length > 1) {
              // Backend feature mismatch: try next base for this request only.
              // Do not put this base on global cooldown, otherwise unrelated
              // endpoints (like payment/order) may be forced to a demo backend.
              continue;
            }

            if (shouldRetryNextBaseForAuthMismatch(endpoint, res.status, orderedBaseUrls)) {
              lastRetriableApiError = new APIError(data.message || 'Request failed', res.status, data);
              continue;
            }

            if (shouldRetryNextBaseForDistancePreview(endpoint, res.status, data, orderedBaseUrls)) {
              lastRetriableApiError = new APIError(data.message || 'Request failed', res.status, data);
              continue;
            }

            if (res.status === 401 && token && typeof window !== 'undefined') {
              localStorage.removeItem('sedyam_token');
              localStorage.removeItem('sedyam_profile');
              if (window.location.pathname !== '/login') window.location.assign('/login');
            }

            throw new APIError(data.message || 'Request failed', res.status, data);
          }

          if (shouldRetryNextBaseForEmptyOperationalOrders(method, endpoint, data, orderedBaseUrls)) {
            continue;
          }

          if (shouldRetryNextBaseForSuccessfulDistancePreview(endpoint, data, orderedBaseUrls)) {
            continue;
          }

          if (shouldRetryNextBaseForLocationDirectoryDistricts(endpoint, data, orderedBaseUrls)) {
            continue;
          }

          rememberPreferredBase(baseUrl, endpoint);
          rememberSessionPreferredBase(baseUrl, endpoint);
          clearBaseFailure(baseUrl);
          return data;
        } catch (err) {
          const isNetworkError =
            err instanceof TypeError ||
            err?.name === 'TypeError' ||
            err?.name === 'AbortError' ||
            /Failed to fetch/i.test(err?.message || '') ||
            /network/i.test(err?.message || '');

          if (!isNetworkError) throw err;
          markBaseFailure(baseUrl);
          lastNetworkError = err;
        }
      }

      if (attempt < maxAttempts - 1) {
        await sleep(RETRY_BACKOFF_MS * (attempt + 1));
      }
    }

    if (lastRetriableApiError) {
      throw lastRetriableApiError;
    }

    const aborted = lastNetworkError?.name === 'AbortError' || /aborted/i.test(lastNetworkError?.message || '');
    const checkedTargets = orderedBaseUrls.join(', ');
    const message = aborted
      ? `Server took too long to respond. Checked: ${checkedTargets}.`
      : `Cannot reach API server. Checked: ${checkedTargets}. Start backend on localhost:5000 or verify deployed backend/CORS.`;

    throw new APIError(message, 0, {
      message,
    });
  };

  const pendingRequest = executeRequest();
  if (requestKey) inflightRequests.set(requestKey, pendingRequest);

  try {
    return await pendingRequest;
  } finally {
    if (requestKey) inflightRequests.delete(requestKey);
  }
};

export const authAPI = {
  register:       (p) => request('POST', '/auth/register', p),
  verifyRegisterOtp: (p) => request('POST', '/auth/register/verify-otp', p),
  login:          (p) => request('POST', '/auth/login', p),
  getMe:          ()  => request('GET',  '/auth/me'),
  updateMe:       (p) => request('PUT',  '/auth/me', p),
  changePassword: (p) => request('PUT',  '/auth/change-password', p),
  requestPasswordResetOtp: (p) => request('POST', '/auth/forgot-password/request-otp', p),
  verifyPasswordResetOtp:  (p) => request('POST', '/auth/forgot-password/verify-otp', p),
  resetPassword:           (p) => request('POST', '/auth/reset-password', p),
  setupSuperAdmin:(p) => request('POST', '/auth/setup-super-admin', p),
  setupAdmin:     (p) => request('POST', '/auth/setup-admin', p),
  createLeadership:(p) => request('POST', '/auth/leadership', p),
};

export const productAPI = {
  getAll: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/products${qs ? '?' + qs : ''}`);
  },
  getById: (id)    => request('GET',    `/products/${id}`),
  create:  (fd)    => request('POST',   '/products', fd, true),
  update:  (id, p, isFormData = false) => request('PUT',    `/products/${id}`, p, isFormData),
  delete:  (id)    => request('DELETE', `/products/${id}`),
  requestUpload: (fd) => request('POST', '/products/upload-requests', fd, true),
  requestEdit: (id, fd) => request('PUT', `/products/${id}/edit-request`, fd, true),
  getEditRequests: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/products/edit-requests${qs ? '?' + qs : ''}`);
  },
  reviewEditRequest: (id, payload) => request('PUT', `/products/edit-requests/${id}/review`, payload),
};

export const orderAPI = {
  getPublicTestimonials: () => request('GET', '/orders/testimonials/public'),
  getAll: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/orders${qs ? '?' + qs : ''}`);
  },
  getById:        (id)               => request('GET',  `/orders/${id}`),
  previewDeliveryDistance: (p)       => request('POST', '/orders/delivery-distance-preview', p),
  create:         (p)                => request('POST', '/orders', p),
  delete:         (id)               => request('DELETE', `/orders/${id}`),
  requestCancellation: (id, payload) => request('POST', `/orders/${id}/request-cancellation`, payload),
  reviewCancellation:  (id, payload) => request('POST', `/orders/${id}/review-cancellation`, payload),
  updateStatus:   (id, newStatus, notes = '') => request('PUT', `/orders/${id}/status`, { newStatus, notes }),
  shareDelivery:  (id, payload)      => request('POST', `/orders/${id}/share-delivery`, payload),
  acceptSharedDelivery: (id, payload = {}) => request('POST', `/orders/${id}/accept-shared-delivery`, payload),
  resendDeliveryOtp: (id) => request('POST', `/orders/${id}/resend-delivery-otp`),
  verifyDelivery: (id, otp)          => request('POST', `/orders/${id}/verify-delivery`, { otp }),
  rateDelivery:   (id, payload)      => request('PUT', `/orders/${id}/rate-delivery`, payload),
};

export const staffAPI = {
  getAll: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/staff${qs ? '?' + qs : ''}`);
  },
  getById: (id) => request('GET', `/staff/${id}`),
  lookupIfsc: (ifsc) => bankingAPI.lookupIfsc(ifsc),
  create:  (p)    => request('POST',   '/staff', p),
  update:  (id,p) => request('PUT',    `/staff/${id}`, p),
  delete:  (id)   => request('DELETE', `/staff/${id}`),
  approve: (id)   => request('PUT',    `/staff/${id}/approve`),
};

export const farmerAPI = {
  getAll: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/farmers${qs ? '?' + qs : ''}`);
  },
  getPublic: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/farmers/public${qs ? '?' + qs : ''}`);
  },
  getPublicById: (id) => request('GET', `/farmers/public/${id}`),
  getMe:   ()       => request('GET',    '/farmers/me'),
  getById: (id)     => request('GET',    `/farmers/${id}`),
  create:  (fd)     => request('POST',   '/farmers', fd, true),
  update:  (id, p)  => request('PUT',    `/farmers/${id}`, p),
  delete:  (id)     => request('DELETE', `/farmers/${id}`),
  approve: (id)     => request('PUT',    `/farmers/${id}/approve`),
};

export const dealerAPI = {
  getAll: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/dealers${qs ? '?' + qs : ''}`);
  },
  getMe: () => request('GET', '/dealers/me'),
  getById: (id) => request('GET', `/dealers/${id}`),
  create: (payload) => request('POST', '/dealers', payload),
  update: (id, payload) => request('PUT', `/dealers/${id}`, payload),
  delete: (id) => request('DELETE', `/dealers/${id}`),
  approve: (id) => request('PUT', `/dealers/${id}/approve`),
};

export const equipmentDealAPI = {
  getAll: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/equipment-deals${qs ? '?' + qs : ''}`);
  },
  getPublic: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/equipment-deals/public${qs ? '?' + qs : ''}`);
  },
  getPublicById: (id) => request('GET', `/equipment-deals/public/${id}`),
  getById: (id) => request('GET', `/equipment-deals/${id}`),
  create: (fd) => request('POST', '/equipment-deals', fd, true),
  update: (id, payload) => request('PUT', `/equipment-deals/${id}`, payload, payload instanceof FormData),
  delete: (id) => request('DELETE', `/equipment-deals/${id}`),
  approve: (id) => request('PUT', `/equipment-deals/${id}/approve`),
};

export const dealAPI = {
  getPublic: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/deals/public${qs ? '?' + qs : ''}`);
  },
  getAll: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/deals${qs ? '?' + qs : ''}`);
  },
  create: (payload) => request('POST', '/deals', payload),
  update: (id, payload) => request('PUT', `/deals/${id}`, payload),
  delete: (id) => request('DELETE', `/deals/${id}`),
};

export const statsAPI = {
  getGlobal:          (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/stats/global${qs ? '?' + qs : ''}`);
  },
  getUserPerformance: (userId) => request('GET', `/stats/user/${userId}`),
  getWebsiteVisitCount: () => request('GET', '/stats/visits'),
  registerWebsiteVisit: () => request('POST', '/stats/visits'),
};

export const paymentAPI = {
  getConfig:   () => request('GET', '/payment/config'),
  createOrder: (payload) => {
    if (payload && typeof payload === 'object') return request('POST', '/payment/create-order', payload);
    return request('POST', '/payment/create-order', { emiPlanId: payload });
  },
  verify:      (p) => request('POST', '/payment/verify', p),
  verifyCheckout: (p) => request('POST', '/payment/verify-checkout', p),
  updateCheckoutRecovery: (id, payload) => request('PUT', `/payment/checkout-recoveries/${id}`, payload),
};

export const emiAPI = {
  create: (p) => request('POST', '/emi/create', p),
};

export const settingsAPI = {
  getFees: () => request('GET', '/settings/fees'),
  updateFees: (payload) => request('PUT', '/settings/fees', payload),
  getServiceZones: () => request('GET', '/settings/service-zones'),
  checkServiceAvailability: (address) => request('POST', '/settings/service-zones/check', { address }),
  updateServiceZones: (payload) => request('PUT', '/settings/service-zones', payload),
  getFeatureFlash: () => request('GET', '/settings/feature-flash'),
  uploadFeatureFlashImage: (formData) => request('POST', '/settings/feature-flash/upload-image', formData, true),
  updateFeatureFlash: (payload) => request('PUT', '/settings/feature-flash', payload),
};

export const farmerInterestAPI = {
  submit: (payload) => request('POST', '/farmer-interest', payload),
  getAll: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/farmer-interest${qs ? '?' + qs : ''}`);
  },
};

export const settlementAPI = {
  getWallet: () => request('GET', '/settlements/wallet'),
  getAdmin: () => request('GET', '/settlements/admin'),
  exportAdmin: (filters = {}) => {
    const qs = new URLSearchParams(filters).toString();
    return request('GET', `/settlements/admin/export${qs ? '?' + qs : ''}`);
  },
  requestInstant: (payload) => request('POST', '/settlements/admin/instant', payload),
  approveInstant: (payload) => request('POST', '/settlements/admin/instant/approve', payload),
};

export const locationDirectoryAPI = {
  getStatus: () => request('GET', '/location-directory/status'),
  getStates: () => request('GET', '/location-directory/states'),
  getDistricts: (state) => request('GET', `/location-directory/districts?${new URLSearchParams({ state: state || '' }).toString()}`),
  getMandals: ({ state, district }) =>
    request('GET', `/location-directory/mandals?${new URLSearchParams({ state: state || '', district: district || '' }).toString()}`),
  getVillages: ({ state, district, mandal }) =>
    request('GET', `/location-directory/villages?${new URLSearchParams({ state: state || '', district: district || '', mandal: mandal || '' }).toString()}`),
  getPincodeAreas: ({ state, district }) =>
    request('GET', `/location-directory/pincode-areas?${new URLSearchParams({ state: state || '', district: district || '' }).toString()}`),
  resolveCurrentLocation: ({ latitude, longitude }) =>
    request('GET', `/location-directory/current-location?${new URLSearchParams({ latitude: latitude || '', longitude: longitude || '' }).toString()}`),
};
